/**
 * Centralized Security Service
 * 
 * This service consolidates all security-related logic to provide:
 * - Consistent permission checking across the application
 * - Centralized BusinessOwner bypass logic
 * - Simplified role-based access control
 * - Better performance through reduced redundant checks
 */

// Role definitions
export const ROLES = {
  BUSINESS_OWNER: 'BusinessOwner',
  INVENTORY_MANAGER: 'InventoryManager',
  ACCOUNTANT: 'Accountant',
  SALESPERSON: 'Salesperson',
  ADMIN: 'Admin'
};

// Permission definitions for different operations
export const PERMISSIONS = {
  // Order management permissions
  CAN_CREATE_ORDERS: [ROLES.SALESPERSON, ROLES.BUSINESS_OWNER, ROLES.ACCOUNTANT],
  CAN_EDIT_ORDERS: [ROLES.SALESPERSON, ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  CAN_APPROVE_ORDERS: [ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  CAN_CANCEL_ORDERS: [ROLES.SALESPERSON, ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  CAN_VIEW_ALL_ORDERS: [ROLES.SALESPERSON, ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER, ROLES.INVENTORY_MANAGER],
  
  // Inventory management permissions
  CAN_MANAGE_PRODUCTS: [ROLES.INVENTORY_MANAGER, ROLES.BUSINESS_OWNER, ROLES.ACCOUNTANT],
  CAN_VIEW_PRODUCTS: [ROLES.INVENTORY_MANAGER, ROLES.BUSINESS_OWNER, ROLES.ACCOUNTANT, ROLES.SALESPERSON],
  CAN_MANAGE_STOCK: [ROLES.INVENTORY_MANAGER, ROLES.BUSINESS_OWNER, ROLES.ACCOUNTANT],
  CAN_VIEW_STOCK: [ROLES.INVENTORY_MANAGER, ROLES.BUSINESS_OWNER, ROLES.ACCOUNTANT, ROLES.SALESPERSON],
  CAN_MANAGE_INVENTORY: [ROLES.INVENTORY_MANAGER, ROLES.BUSINESS_OWNER, ROLES.ACCOUNTANT], // Combined permission
  CAN_VIEW_INVENTORY: [ROLES.INVENTORY_MANAGER, ROLES.BUSINESS_OWNER, ROLES.ACCOUNTANT, ROLES.SALESPERSON], // Combined permission
  CAN_DISPATCH_ORDERS: [ROLES.INVENTORY_MANAGER, ROLES.BUSINESS_OWNER],
  CAN_READY_TO_DISPATCH: [ROLES.INVENTORY_MANAGER, ROLES.BUSINESS_OWNER],

  // Payments & Dues: accountants and owners punch sales/payments;
  // salespeople only view their own (enforced by query + Firestore rules)
  CAN_MANAGE_RECEIVABLES: [ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  CAN_VIEW_RECEIVABLES: [ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER, ROLES.SALESPERSON],
  
  // User management permissions
  CAN_MANAGE_USERS: [ROLES.BUSINESS_OWNER],
  CAN_VIEW_USER_MANAGEMENT: [ROLES.BUSINESS_OWNER],
  
  // File upload permissions by type
  CAN_UPLOAD_BILLS: [ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  CAN_UPLOAD_CHALLANS: [ROLES.INVENTORY_MANAGER, ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  CAN_UPLOAD_BILTYS: [ROLES.INVENTORY_MANAGER, ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  CAN_UPLOAD_PRODUCTS: [ROLES.INVENTORY_MANAGER, ROLES.BUSINESS_OWNER],
  CAN_UPLOAD_ORDERS: [ROLES.SALESPERSON, ROLES.BUSINESS_OWNER],
  // Note: Invoices are defined in validationUtils but not actually implemented in the UI
  // Keeping for potential future use, but currently unused
  CAN_UPLOAD_INVOICES: [ROLES.SALESPERSON, ROLES.BUSINESS_OWNER],
  
  // Combined file upload permissions for easier use
  CAN_UPLOAD_BILLS_OR_CHALLANS: [ROLES.INVENTORY_MANAGER, ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  CAN_UPLOAD_ANY_FILE: [ROLES.BUSINESS_OWNER], // BusinessOwner can upload any file type
  
  // File viewing permissions
  CAN_VIEW_BILLS: [ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  CAN_VIEW_CHALLANS: [ROLES.INVENTORY_MANAGER, ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  CAN_VIEW_BILTYS: [ROLES.INVENTORY_MANAGER, ROLES.ACCOUNTANT, ROLES.BUSINESS_OWNER],
  // Note: CAN_VIEW_PRODUCTS is defined above in Inventory management permissions section (line 31)
  // and includes SALESPERSON access for viewing products in inventory
  
  // Dashboard and navigation permissions
  CAN_VIEW_DASHBOARD: [ROLES.BUSINESS_OWNER],
  CAN_VIEW_ACCOUNT_PLAN: [ROLES.BUSINESS_OWNER],
  CAN_VIEW_ANALYTICS: [ROLES.BUSINESS_OWNER, ROLES.ACCOUNTANT]
};

// File type to permission mapping
export const FILE_TYPE_PERMISSIONS = {
  'bill': PERMISSIONS.CAN_UPLOAD_BILLS,           // ✅ IMPLEMENTED - Financial documents (what customer owes)
  'challan': PERMISSIONS.CAN_UPLOAD_CHALLANS,     // ✅ IMPLEMENTED - Delivery challans
  'bilty': PERMISSIONS.CAN_UPLOAD_BILTYS,         // ✅ IMPLEMENTED - Transport receipts
  'product': PERMISSIONS.CAN_UPLOAD_PRODUCTS,     // ✅ IMPLEMENTED - Product images
  'order': PERMISSIONS.CAN_UPLOAD_ORDERS,         // ✅ IMPLEMENTED - Order details images
  'invoice': PERMISSIONS.CAN_UPLOAD_INVOICES      // ❌ NOT IMPLEMENTED - Defined but no UI/storage logic
};

class SecurityService {
  /**
   * Check if a user has permission for a specific operation
   * @param {string} operation - The operation to check (e.g., 'CAN_EDIT_ORDERS')
   * @param {string} userRole - The user's role
   * @returns {boolean} - Whether the user has permission
   */
  static hasPermission(operation, userRole) {
    if (!userRole) return false;
    
    // BusinessOwner has access to everything
    if (userRole === ROLES.BUSINESS_OWNER) {
      return true;
    }
    
    const allowedRoles = PERMISSIONS[operation];
    if (!allowedRoles) {
      return false;
    }
    
    return allowedRoles.includes(userRole);
  }

  /**
   * Check if a user can upload a specific file type
   * @param {string} fileType - The type of file to upload
   * @param {string} userRole - The user's role
   * @returns {boolean} - Whether the user can upload this file type
   */
  static canUploadFileType(fileType, userRole) {
    if (!userRole || !fileType) return false;
    
    // BusinessOwner can upload all file types
    if (userRole === ROLES.BUSINESS_OWNER) {
      return true;
    }
    
    const allowedRoles = FILE_TYPE_PERMISSIONS[fileType];
    if (!allowedRoles) {
      return false;
    }
    
    return allowedRoles.includes(userRole);
  }

  /**
   * Check if a user can view bills (special case for inventory managers)
   * @param {string} userRole - The user's role
   * @param {boolean} allowInventoryManagerBillAccess - Organization setting
   * @returns {boolean} - Whether the user can view bills
   */
  static canViewBills(userRole, allowInventoryManagerBillAccess = true) {
    if (!userRole) return false;
    
    // BusinessOwner and Accountant can always view bills
    if (userRole === ROLES.BUSINESS_OWNER || userRole === ROLES.ACCOUNTANT) {
      return true;
    }
    
    // InventoryManager can view bills if organization allows it
    if (userRole === ROLES.INVENTORY_MANAGER) {
      return allowInventoryManagerBillAccess;
    }
    
    return false;
  }

  /**
   * Check if a user can edit a specific order
   * @param {string} userRole - The user's role
   * @param {string} orderCreatedBy - The user who created the order
   * @param {string} orderAssignedTo - The user assigned to the order
   * @param {string} currentUserId - The current user's ID
   * @returns {boolean} - Whether the user can edit this order
   */
  static canEditOrder(userRole, orderCreatedBy, orderAssignedTo, currentUserId) {
    if (!userRole || !currentUserId) return false;
    
    // BusinessOwner and Accountant can edit any order
    if (userRole === ROLES.BUSINESS_OWNER || userRole === ROLES.ACCOUNTANT) {
      return true;
    }
    
    // Salesperson can edit orders they created or are assigned to
    if (userRole === ROLES.SALESPERSON) {
      return orderCreatedBy === currentUserId || orderAssignedTo === currentUserId;
    }
    
    return false;
  }

  /**
   * Check if a user can cancel a specific order
   * @param {string} userRole - The user's role
   * @param {string} orderCreatedBy - The user who created the order
   * @param {string} orderAssignedTo - The user assigned to the order
   * @param {string} currentUserId - The current user's ID
   * @returns {boolean} - Whether the user can cancel this order
   */
  static canCancelOrder(userRole, orderCreatedBy, orderAssignedTo, currentUserId) {
    // Same logic as editing orders
    return this.canEditOrder(userRole, orderCreatedBy, orderAssignedTo, currentUserId);
  }

  /**
   * Get the required roles for a file type upload
   * @param {string} fileType - The type of file
   * @returns {string[]} - Array of roles that can upload this file type
   */
  static getRequiredRolesForFileType(fileType) {
    return FILE_TYPE_PERMISSIONS[fileType] || [];
  }

  /**
   * Check if a user has any of the required roles
   * @param {string[]} requiredRoles - Array of roles to check against
   * @param {string} userRole - The user's role
   * @returns {boolean} - Whether the user has any of the required roles
   */
  static hasAnyRole(requiredRoles, userRole) {
    if (!userRole || !Array.isArray(requiredRoles)) return false;
    
    // BusinessOwner has access to everything
    if (userRole === ROLES.BUSINESS_OWNER) {
      return true;
    }
    
    return requiredRoles.includes(userRole);
  }

  /**
   * Validate user access for an operation with detailed error message
   * @param {string} operation - The operation to check
   * @param {string} userRole - The user's role
   * @param {Object} context - Additional context for the operation
   * @returns {Object} - { hasAccess: boolean, errorMessage?: string }
   */
  static validateAccess(operation, userRole, context = {}) {
    if (!userRole) {
      return {
        hasAccess: false,
        errorMessage: 'User role not found. Please log in again.'
      };
    }

    if (!this.hasPermission(operation, userRole)) {
      const allowedRoles = PERMISSIONS[operation] || [];
      const roleNames = allowedRoles.map(role => role.replace(/([A-Z])/g, ' $1').trim());
      
      return {
        hasAccess: false,
        errorMessage: `Access denied. This operation requires one of the following roles: ${roleNames.join(', ')}`
      };
    }

    return { hasAccess: true };
  }

  /**
   * Get all permissions for a user role
   * @param {string} userRole - The user's role
   * @returns {Object} - Object with all permissions for the role
   */
  static getRolePermissions(userRole) {
    if (!userRole) return {};
    
    const permissions = {};
    
    // Check each permission
    Object.keys(PERMISSIONS).forEach(permission => {
      permissions[permission] = this.hasPermission(permission, userRole);
    });
    
    return permissions;
  }
}

export default SecurityService;
